import { useEffect, useRef, useState } from "react";
import { Marker } from "react-leaflet";
import type { DragEndEvent } from "leaflet";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Info, MapPin } from "lucide-react";
import { getAddressSuggestions, reverseGeocode } from "@/api/geocoding";
import { AddressHint } from "@/components/ui/AddressHint";
import OsmMap from "@/components/map/OsmMap";
import { MapClickCapture } from "@/components/map/MapClickCapture";
import { RecenterMap } from "@/components/map/RecenterMap";
import { useMapCenterLadder } from "@/features/orders/hooks/useMapCenterLadder";
import { createBrandedPinIcon } from "@/lib/mapIcons";
import { extractApiErrorMessage, readApiErrorStatus } from "@/lib/apiErrors";
import { cn } from "@/lib/cn";

/** A single Nominatim suggestion returned by the autocomplete. */
export interface GeocodeSuggestion {
  lat: string;
  lon: string;
  display_name: string;
}

export const GEOCODE_DEBOUNCE_MS = 400;
/**
 * Reverse is deliberately slower than forward (600 vs 400 ms): a drag emits one
 * `dragend`, but a dispatcher nudging a pin into place emits several in quick
 * succession — and every one of them is a paid provider call against a budget
 * the whole platform shares (TASK-4996).
 */
export const REVERSE_GEOCODE_DEBOUNCE_MS = 600;
export const GEOCODE_MIN_CHARS = 3;
export const GEOCODE_MAX_RESULTS = 5;

/**
 * The status line under the address field (§5.1).
 *
 * `notFound` and `failed` are deliberately DIFFERENT states: the first means
 * "fix the address", the second means "try again". Collapsing them is the
 * defect TASK-4745 already fixed on the detail modal — it must not be
 * re-introduced here.
 *
 * The reverse family (TASK-4996) keeps the same distinction for the same
 * reason, and adds one more the forward leg has no equivalent of:
 * `reverseRateLimited` means "you are going too fast", which is advice to WAIT,
 * not to fix the coordinates. Folding it into `reverseFailed` would tell the
 * dispatcher to retry immediately — the one action that makes it worse.
 */
export type GeocodeStatus =
  | "idle"
  | "searching"
  | "found"
  | "notFound"
  | "failed"
  | "manual"
  | "reverseSearching"
  | "reverseFilled"
  | "reverseSuggested"
  | "reverseNotFound"
  | "reverseRateLimited"
  | "reverseFailed";

interface Props {
  /** Current address text. */
  address: string;
  onAddressChange: (next: string) => void;
  /** Latitude as a raw string — kept a string so a half-typed "53." is not destroyed. */
  lat: string;
  lon: string;
  onCoordsChange: (lat: string, lon: string) => void;
  /**
   * Unique prefix for every id/`aria-*` on this block. Two address blocks can be
   * mounted at once (an order modal handing off to a template modal), and the
   * BUG-504/508 family is precisely what happens when they share names.
   */
  idPrefix: string;
  /** Field-level errors from the host form's zod schema. */
  addressError?: string;
  latError?: string;
  lonError?: string;
  /** Address `maxLength` — 512 for an Order, 500 for an OrderTemplate (§12.1). */
  maxLength?: number;
  /** Rendered below the coordinates — the create modals pass their ConfirmationBlock. */
  children?: React.ReactNode;
  /**
   * Let the map absorb the column's leftover height instead of standing at its
   * fixed `h-36`/`2xl:h-56` (owner, 2026-08-25: «справа где адрес увеличить
   * карту по длине, важно чтобы все нижние поля … были внизу по одной линии»).
   *
   * The map is the only element in the dialog with no natural height — every
   * other control is as tall as its content — so it is the honest place to put
   * the slack that makes the three columns finish on one line. Growing anything
   * else would mean inventing height for a field that does not want it.
   *
   * Opt-in, because it only makes sense where this block is the WHOLE column and
   * the column is a full-height flex parent. The template and order-detail
   * modals render it among other blocks, where a growing map would eat the
   * space their remaining fields need; they stay on the fixed height.
   */
  growMap?: boolean;
}

/**
 * Address autocomplete + map + pin + coordinates — one implementation, shared by
 * the regular-order and permanent-order forms.
 *
 * There is deliberately **no `mode` prop** (owner ruling, §1.1.1). The template
 * forms' «Найти» button is gone; the address resolves as you type on every
 * surface. That flag was the exact seam the two implementations drifted through,
 * so re-introducing it would recreate the bug this component exists to close.
 */
export function AddressMapBlock({
  address,
  onAddressChange,
  lat,
  lon,
  onCoordsChange,
  idPrefix,
  addressError,
  latError,
  lonError,
  maxLength = 512,
  children,
  growMap = false,
}: Props) {
  const { t } = useTranslation();

  const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [status, setStatus] = useState<GeocodeStatus>("idle");
  const [failureDetail, setFailureDetail] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The query the last request was fired for — powers [Повторить] without retyping. */
  const lastQueryRef = useRef<string>("");

  /**
   * The address the reverse lookup resolved while the field was NOT empty.
   * Offered as a dismissible suggestion and never applied on its own — the
   * dispatcher's own wording wins (owner ruling, TASK-4996 §1). This is what
   * protects hand-added detail like «подъезд 3, код 45» that no geocoder can
   * reproduce.
   */
  const [reverseSuggestion, setReverseSuggestion] = useState<string | null>(
    null,
  );
  const reverseDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Monotonic id of the newest reverse request. An answer whose id is stale is
   * dropped on arrival, so a slow first lookup cannot overwrite the address a
   * faster second one already produced.
   */
  const reverseRequestIdRef = useRef(0);
  /** Coordinates of the last reverse request — powers retry without re-dragging. */
  const lastReverseCoordsRef = useRef<{ lat: number; lon: number } | null>(
    null,
  );

  const latNum = parseFloat(lat ?? "");
  const lonNum = parseFloat(lon ?? "");
  /**
   * `0,0` counts as "not set" — the existing convention across the app. Without
   * it a blank field (`Number("")` is `0`) drops a pin in the Gulf of Guinea and
   * asserts a location the order does not have.
   */
  const hasCoords =
    isFinite(latNum) &&
    isFinite(lonNum) &&
    lat.trim() !== "" &&
    lon.trim() !== "" &&
    latNum !== 0 &&
    lonNum !== 0 &&
    latNum >= -90 &&
    latNum <= 90 &&
    lonNum >= -180 &&
    lonNum <= 180;

  /**
   * What an empty (markerless) map is centred on — the tenant ladder of
   * TASK-4997, never a hardcoded city. With coordinates present it returns
   * exactly today's view (the order's own point at zoom 14), so an order that
   * has a pin is unaffected.
   *
   * The `hasShownMapRef` guard that used to live here is gone with the branch
   * it protected: it existed because the map MOUNTED and UNMOUNTED as
   * coordinates came and went, and unmounting a Leaflet map with a draggable
   * marker mid close-animation crashes inside Leaflet. The map is now mounted
   * unconditionally for the block's whole life and only the `Marker` toggles,
   * so there is no map teardown to guard against.
   */
  const { center: ladderCenter, zoom: mapZoom } = useMapCenterLadder({
    lat: hasCoords ? latNum : null,
    lon: hasCoords ? lonNum : null,
    enabled: true,
  });
  /**
   * The ladder always yields a centre now — its last rung is the operating
   * region (OWNER, 2026-09-24), so there is no un-centred case left to guard.
   * No marker is rendered unless the order actually has coordinates, so framing
   * the map asserts nothing about where the delivery is.
   */
  const mapCenter: [number, number] = ladderCenter;

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (reverseDebounceRef.current) clearTimeout(reverseDebounceRef.current);
    };
  }, []);

  async function runGeocode(query: string): Promise<void> {
    lastQueryRef.current = query;
    if (query.length < GEOCODE_MIN_CHARS) {
      setSuggestions([]);
      setDropdownOpen(false);
      setStatus("idle");
      return;
    }
    setStatus("searching");
    setFailureDetail(null);
    try {
      const results = await getAddressSuggestions(query, GEOCODE_MAX_RESULTS);
      setSuggestions(results);
      if (results.length === 0) {
        setDropdownOpen(false);
        setStatus("notFound");
        return;
      }
      setDropdownOpen(true);
      setActiveIndex(-1);
      setStatus("found");
    } catch (err) {
      // BUG-464: never `err.message` — an axios error IS an Error, so `.message`
      // is the transport string ("Request failed with status code 500"), not
      // anything the server said.
      setFailureDetail(extractApiErrorMessage(err, t));
      setSuggestions([]);
      setDropdownOpen(false);
      setStatus("failed");
    }
  }

  function handleAddressInput(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    onAddressChange(value);
    setFailureDetail(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length >= GEOCODE_MIN_CHARS) {
      setStatus("searching");
      debounceRef.current = setTimeout(() => {
        void runGeocode(value);
      }, GEOCODE_DEBOUNCE_MS);
    } else {
      setSuggestions([]);
      setDropdownOpen(false);
      setStatus("idle");
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!dropdownOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      const selected = suggestions[activeIndex];
      if (selected) selectSuggestion(selected);
    } else if (e.key === "Escape") {
      // Closes the dropdown ONLY — the dialog must stay open, otherwise
      // dismissing an autocomplete throws away the whole half-filled form.
      e.preventDefault();
      e.stopPropagation();
      setDropdownOpen(false);
      setActiveIndex(-1);
    }
  }

  function selectSuggestion(suggestion: GeocodeSuggestion) {
    onAddressChange(suggestion.display_name);
    onCoordsChange(
      parseFloat(suggestion.lat).toFixed(6),
      parseFloat(suggestion.lon).toFixed(6),
    );
    setSuggestions([]);
    setDropdownOpen(false);
    setActiveIndex(-1);
    setStatus("found");
  }

  /**
   * Resolves coordinates to an address (BUG-587). Every exit path leaves the
   * existing `address` value untouched unless the field is EMPTY — a failed
   * lookup must never blank a field the dispatcher filled.
   */
  async function runReverseGeocode(
    latValue: number,
    lonValue: number,
  ): Promise<void> {
    const requestId = ++reverseRequestIdRef.current;
    lastReverseCoordsRef.current = { lat: latValue, lon: lonValue };
    setStatus("reverseSearching");
    setFailureDetail(null);
    setReverseSuggestion(null);

    /** A superseded answer is discarded rather than applied (stale guard). */
    const isCurrent = () => reverseRequestIdRef.current === requestId;

    try {
      const result = await reverseGeocode(latValue, lonValue);
      if (!isCurrent()) return;
      if (result === null) {
        // 422 — the provider matched no location here. That is NOT a failure to
        // retry, and it is NOT a reason to touch the address.
        setStatus("reverseNotFound");
        return;
      }
      if (address.trim() === "") {
        // The main case: a coordinates-only import, or a point just placed by
        // hand. Nothing to protect, so fill it directly — no suggestion step.
        onAddressChange(result.address);
        setStatus("reverseFilled");
        return;
      }
      // Something is already typed. Offer, never overwrite.
      setReverseSuggestion(result.address);
      setStatus("reverseSuggested");
    } catch (err) {
      if (!isCurrent()) return;
      // 429 is the throttle, not a broken lookup: it means "wait", and telling
      // the dispatcher to retry right now is the one piece of advice that makes
      // it worse. Everything else is a genuine failure with a retry affordance.
      if (readApiErrorStatus(err) === 429) {
        setStatus("reverseRateLimited");
        return;
      }
      // BUG-464: never `err.message` — an axios error IS an Error, so `.message`
      // is the transport string, not anything the server said.
      setFailureDetail(extractApiErrorMessage(err, t));
      setStatus("reverseFailed");
    }
  }

  /**
   * Debounced entry point shared by BOTH ways a point can move — marker drag,
   * map click and manual coordinate entry all land here, so there is exactly
   * one reverse code path (TASK-4997) and one timer bounding it.
   */
  function scheduleReverseGeocode(latText: string, lonText: string) {
    if (reverseDebounceRef.current) clearTimeout(reverseDebounceRef.current);

    const latValue = parseFloat(latText);
    const lonValue = parseFloat(lonText);
    const usable =
      isFinite(latValue) &&
      isFinite(lonValue) &&
      // Null island is the PAIR 0/0, not either axis alone. `lat !== 0 && lon !== 0`
      // silently refused every point on the equator or the prime meridian — both
      // legitimate. Every other layer already pairs them: signals.py:66,
      // tasks.py:313, services.py:508.
      !(latValue === 0 && lonValue === 0) &&
      latValue >= -90 &&
      latValue <= 90 &&
      lonValue >= -180 &&
      lonValue <= 180;
    if (!usable) {
      // A half-typed "53." is not a location; say nothing and spend nothing.
      // Bumping the id cancels any in-flight answer for the old coordinates.
      reverseRequestIdRef.current += 1;
      setReverseSuggestion(null);
      setStatus("manual");
      return;
    }

    setStatus("reverseSearching");
    reverseDebounceRef.current = setTimeout(() => {
      void runReverseGeocode(latValue, lonValue);
    }, REVERSE_GEOCODE_DEBOUNCE_MS);
  }

  /** Applies the offered address — the explicit accept the owner requires. */
  function acceptReverseSuggestion() {
    if (reverseSuggestion === null) return;
    onAddressChange(reverseSuggestion);
    setReverseSuggestion(null);
    setStatus("reverseFilled");
  }

  function dismissReverseSuggestion() {
    setReverseSuggestion(null);
    setStatus("manual");
  }

  function applyPoint(latValue: number, lonValue: number) {
    const latText = latValue.toFixed(6);
    const lonText = lonValue.toFixed(6);
    onCoordsChange(latText, lonText);
    scheduleReverseGeocode(latText, lonText);
  }

  function handleDragEnd(e: DragEndEvent) {
    const latlng = e.target.getLatLng();
    applyPoint(latlng.lat, latlng.lng);
  }

  /** Click-to-place on the empty map — the same path a drag takes (TASK-4997). */
  function handleMapPlace(latValue: number, lonValue: number) {
    applyPoint(latValue, lonValue);
  }

  function handleCoordInput(which: "lat" | "lon", value: string) {
    const nextLat = which === "lat" ? value : lat;
    const nextLon = which === "lon" ? value : lon;
    onCoordsChange(nextLat, nextLon);
    scheduleReverseGeocode(nextLat, nextLon);
  }

  const listboxId = `${idPrefix}-address-listbox`;
  const statusId = `${idPrefix}-geocode-status`;

  /**
   * Exactly one coordinate filled — the pair is meaningless, so say which one is
   * missing rather than silently hiding the map (§5.2).
   */
  const oneCoordBlank =
    (lat.trim() === "") !== (lon.trim() === "") && addressError === undefined;

  return (
    /*
      `growMap` turns the block into a full-height flex column so the map — the
      one element with no intrinsic height — can claim the leftover with
      `flex-1`. `gap-3` reproduces `space-y-3` exactly, so the non-growing
      layout is byte-for-byte the same spacing it always had.
    */
    <div
      className={cn(
        // `flex-1`, NOT `h-full`: this block is a flex ITEM of the column, and
        // the column also holds an <h3> plus a `gap-2.5`. `height:100%` resolves
        // against the column's full content box, so it overshoots by the heading
        // + gap, and — being `flex: 0 1 auto` — the block is then shrunk back to
        // its CONTENT height (530px against a 589px column). The map's `flex-1`
        // was working the whole time; it was dividing up a parent that had
        // already collapsed. `flex-1` claims exactly the space the heading
        // leaves, which is what makes the column reach the other two.
        growMap ? "flex min-h-0 flex-1 flex-col gap-3" : "space-y-3",
      )}
    >
      <div className="relative">
        <label
          htmlFor={`${idPrefix}-address`}
          className="mb-1 block text-xs font-semibold text-gray-700"
        >
          {t("orders.form.address")}{" "}
          <span className="text-brand-danger" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id={`${idPrefix}-address`}
          value={address}
          onChange={handleAddressInput}
          onFocus={() => {
            if (suggestions.length > 0) setDropdownOpen(true);
          }}
          onBlur={() =>
            setTimeout(() => {
              setDropdownOpen(false);
              setActiveIndex(-1);
            }, 150)
          }
          onKeyDown={handleKeyDown}
          placeholder={t("orders.form.addressPlaceholder")}
          maxLength={maxLength}
          autoComplete="off"
          role="combobox"
          aria-expanded={dropdownOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-describedby={statusId}
          className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
        />
        {(status === "searching" || status === "reverseSearching") && (
          <span className="absolute right-3 top-[34px]">
            <span className="block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-brand-primary" />
          </span>
        )}

        {dropdownOpen && suggestions.length > 0 && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label={t("orders.form.address")}
            className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg"
          >
            {suggestions.map((suggestion, index) => (
              <li key={`${suggestion.lat}-${suggestion.lon}-${index}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={activeIndex === index}
                  onMouseDown={() => selectSuggestion(suggestion)}
                  className={cn(
                    "w-full break-words px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-brand-primary/10",
                    activeIndex === index && "bg-brand-primary/10",
                  )}
                >
                  {suggestion.display_name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {addressError && (
        <p className="text-xs text-brand-danger">{addressError}</p>
      )}

      {/*
        The geocode status line (§5.1). It reflects the REAL result — the
        mockup's «точка найдена по адресу» is one of six states, never a
        hardcoded string. `aria-live` so a screen-reader user hears the outcome
        of a search they did not click for.
      */}
      <p id={statusId} aria-live="polite" className="min-h-[1rem] text-xs">
        {status === "searching" && (
          <span className="text-gray-500">
            {t("orders.form.geocode.searching")}
          </span>
        )}
        {status === "found" && hasCoords && (
          <span className="inline-flex items-center gap-1 text-brand-success">
            <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t("orders.form.geocode.found")}
          </span>
        )}
        {status === "notFound" && (
          <span className="inline-flex items-start gap-1 text-brand-warning">
            <AlertTriangle
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            <span>{t("orders.form.geocode.notFound")}</span>
          </span>
        )}
        {status === "failed" && (
          <span className="inline-flex flex-wrap items-start gap-1 text-brand-danger">
            <AlertTriangle
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            <span>
              {t("orders.form.geocode.failed")}
              {failureDetail ? ` — ${failureDetail}` : ""}
            </span>
            <button
              type="button"
              onClick={() => void runGeocode(lastQueryRef.current || address)}
              className="font-semibold underline hover:no-underline"
            >
              {t("common.retry")}
            </button>
          </span>
        )}
        {status === "manual" && hasCoords && (
          <span className="inline-flex items-center gap-1 text-gray-500">
            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t("orders.form.geocode.manual")}
          </span>
        )}
        {status === "reverseSearching" && (
          <span className="text-gray-500">
            {t("orders.form.geocode.reverseSearching")}
          </span>
        )}
        {status === "reverseFilled" && (
          <span className="inline-flex items-center gap-1 text-brand-success">
            <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t("orders.form.geocode.reverseFilled")}
          </span>
        )}
        {/* "No address here" — advice to move the pin, never to retry. */}
        {status === "reverseNotFound" && (
          <span className="inline-flex items-start gap-1 text-brand-warning">
            <AlertTriangle
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            <span>{t("orders.form.geocode.reverseNotFound")}</span>
          </span>
        )}
        {/* "Too fast" — advice to WAIT. Deliberately carries no retry button:
            retrying immediately is the one action that makes a throttle worse. */}
        {status === "reverseRateLimited" && (
          <span className="inline-flex items-start gap-1 text-brand-warning">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{t("orders.form.geocode.reverseRateLimited")}</span>
          </span>
        )}
        {status === "reverseFailed" && (
          <span className="inline-flex flex-wrap items-start gap-1 text-brand-danger">
            <AlertTriangle
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            <span>
              {t("orders.form.geocode.reverseFailed")}
              {failureDetail ? ` — ${failureDetail}` : ""}
            </span>
            <button
              type="button"
              onClick={() => {
                const last = lastReverseCoordsRef.current;
                if (last) void runReverseGeocode(last.lat, last.lon);
              }}
              className="font-semibold underline hover:no-underline"
            >
              {t("common.retry")}
            </button>
          </span>
        )}
      </p>

      {/*
        The suggestion line (owner ruling, TASK-4996 §1). It appears ONLY when
        the address field already had text: «На карте: <адрес> — Подставить».
        The typed value is untouched until the dispatcher accepts.
      */}
      {status === "reverseSuggested" && reverseSuggestion !== null && (
        <div className="flex flex-wrap items-start gap-x-2 gap-y-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
          <span className="inline-flex min-w-0 items-start gap-1">
            <MapPin
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400"
              aria-hidden="true"
            />
            <span className="break-words">
              {t("orders.form.geocode.reverseSuggestionLabel")}{" "}
              <span className="font-medium text-gray-900">
                {reverseSuggestion}
              </span>
            </span>
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={acceptReverseSuggestion}
              className="font-semibold text-brand-primary underline hover:no-underline"
            >
              {t("orders.form.geocode.reverseSuggestionAccept")}
            </button>
            <button
              type="button"
              onClick={dismissReverseSuggestion}
              className="text-gray-500 underline hover:no-underline"
            >
              {t("common.dismiss")}
            </button>
          </span>
        </div>
      )}

      {/* BUG-422 — the hint expands itself exactly when geocoding fails. */}
      {(status === "notFound" || status === "failed") && (
        <AddressHint forceOpen />
      )}

      {/*
        The map is ALWAYS present (BUG-586/TASK-4997), with no marker until the
        user places one.

        The old reasoning — "never a map centred on a default city: that would
        assert a location the order does not have" — was right about the MARKER
        and wrong about the MAP. An empty map asserts nothing; it is a tool, not
        a claim, and it is the only place a dispatcher who knows WHERE but not
        the street can point at the location. What is centred is resolved by the
        tenant ladder in `useMapCenterLadder` — the tenant's own depot, depot
        centroid or configured area — so the view describes the TENANT and never
        a hardcoded city.
      */}
      <div
        className={cn(
          "relative w-full overflow-hidden rounded-xl",
          // Growing: a floor of the old h-36 so the map is never smaller than
          // it used to be, then `flex-1` for everything the column has spare.
          // Identical to what the placeholder used, so the three-column layout
          // does not jump now that the map stands in both states.
          growMap ? "min-h-[9rem] flex-1" : "h-36 2xl:h-56",
        )}
      >
        <OsmMap
          center={mapCenter}
          zoom={mapZoom}
          className="h-full w-full"
          data-testid={`${idPrefix}-map`}
        >
          {hasCoords ? (
            <RecenterMap lat={latNum} lon={lonNum} />
          ) : (
            /* The markerless map needs recentring too. `MapContainer` applies
               `center`/`zoom` ONLY at mount, and the ladder's rungs resolve
               asynchronously — the warehouse queries land after first render —
               so without this the first order of a session keeps whichever rung
               was ready at mount (the region fallback) and never corrects. Pass
               the zoom as well: the rungs differ in scale. */
            <RecenterMap lat={mapCenter[0]} lon={mapCenter[1]} zoom={mapZoom} />
          )}
          <MapClickCapture onPlace={handleMapPlace} />
          {/* No marker at all until a point exists — not a hidden or
              transparent one. */}
          {hasCoords && (
            <Marker
              position={[latNum, lonNum]}
              icon={createBrandedPinIcon()}
              draggable={true}
              eventHandlers={{ dragend: handleDragEnd }}
            />
          )}
        </OsmMap>
        {/* So an empty map is not mistaken for a broken one. `pointer-events-none`
            keeps the hint from swallowing the very click it asks for. */}
        {!hasCoords && (
          /* Sits ABOVE the attribution band, not beside it. Reserving a fixed
             right margin was tried and failed: the OSM credit's width varies
             with locale and zoom, so any fixed inset is wrong at some width and
             the hint gets clipped again. `bottom-6` clears the ~20px credit row
             outright, so the two never share a line whatever the credit says.
             `z-[900]` beats Leaflet's control z-index (800). */
          <p className="pointer-events-none absolute inset-x-0 bottom-6 z-[900] px-3 text-center text-xs text-gray-700">
            <span className="rounded bg-white/90 px-2 py-1 shadow-sm">
              {t("orders.form.geocode.clickToPlace")}
            </span>
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0">
          <label
            htmlFor={`${idPrefix}-lat`}
            className="mb-1 block text-xs font-semibold text-gray-700"
          >
            {t("orders.form.latitude")}
          </label>
          <input
            id={`${idPrefix}-lat`}
            value={lat}
            onChange={(e) => handleCoordInput("lat", e.target.value)}
            inputMode="decimal"
            placeholder="53.9045"
            className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm tabular-nums text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
          />
          {latError && (
            <p className="mt-1 text-xs text-brand-danger">{latError}</p>
          )}
        </div>
        <div className="min-w-0">
          <label
            htmlFor={`${idPrefix}-lon`}
            className="mb-1 block text-xs font-semibold text-gray-700"
          >
            {t("orders.form.longitude")}
          </label>
          <input
            id={`${idPrefix}-lon`}
            value={lon}
            onChange={(e) => handleCoordInput("lon", e.target.value)}
            inputMode="decimal"
            placeholder="27.5615"
            className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm tabular-nums text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
          />
          {lonError && (
            <p className="mt-1 text-xs text-brand-danger">{lonError}</p>
          )}
        </div>
      </div>

      {oneCoordBlank && !latError && !lonError && (
        <p className="text-xs text-brand-warning">
          {t("orders.form.geocode.bothCoords")}
        </p>
      )}

      {children}
    </div>
  );
}
